import React from 'react';
import styles from './Subscription.module.css';
import StatusBadge from '@/components/StatusBadge/StatusBadge';
import Divider from '@/components/Divider/Divider';
import { IntervalUnit } from '@boxem/core-api-schema';
import { useUserContext } from '@/contexts/UserContext';
import { UserRole } from '@boxem/core-api-schema';
import Tooltip from '@/components/Tooltip';

const SubscriptionCard = ({ 
  plan = 'Professional - Launch Special',
  isActive = true,
  state = 'active',
  billingCycle = IntervalUnit.MONTHLY,
  currentPeriod = { start: 'Mar 15, 2025', end: 'Apr 25, 2025' },
  termBehaviour = 'Auto Renewing',
  collection = 'Automatic',
  renewsOn = 'Apr 25, 2025 12:57 PM',
  startedOn = 'Oct 25, 2024 12:56 PM',
  latestInvoice = '#10416',
  nextInvoiceDate = 'Apr 25, 2025',
  lineItems = [{ description: 'Professional - Launch Special', qty: 1, price: 69.99 }],
  currency = 'USD',
  isThereNextTier = true,
  
  onUpgrade = (subscriptionType: string, billingPeriod: string) => console.log('Upgrade clicked', subscriptionType, billingPeriod),
  onCancel = () => console.log('Cancel clicked'),
  onResubscribe = () => console.log('Resubscribe clicked'),
  handleViewInvoice = () => console.log('View invoice clicked')
}) => {
  const total = lineItems.reduce((sum, item) => sum + (item.price * (item.qty || 1)), 0);
  const { user } = useUserContext();
  
  // Extract subscription type from plan name
  const planNameLower = plan.toLowerCase();
  const isStandard = planNameLower.includes('standard');
  const isProfessional = planNameLower.includes('professional');
  
  // Determine button visibility based on plan and billing cycle
  const showUpgradeToAnnual = billingCycle === IntervalUnit.MONTHLY;
  
  // Check subscription states
  const isCanceled = state === 'canceled';
  const isExpired = state === 'expired';
  
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div>
          <div className={styles.titleRow}>
            <h2 className={styles.title}>{plan}</h2>
            {isCanceled ? (
              <StatusBadge status="danger" label="Canceled" className={styles.canceledStatus} />
            ) : isExpired ? (
              <StatusBadge status="warning" label="Expired" className={styles.expiredStatus} />
            ) : isActive ? (
              <StatusBadge status="success" label="Active" className={styles.activeStatus} />
            ) : null}
          </div>
          <div className={styles.billingCycle}>
            Billed {billingCycle === IntervalUnit.MONTHLY ? 'monthly' : 'annually'}
          </div>
        </div>
        {!isCanceled && !isExpired && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {isThereNextTier && (
              <button 
                className={styles.upgradeButton} 
                onClick={() => onUpgrade('professional', billingCycle)}
                disabled={user?.role === UserRole.ASSISTANT}
              >
                Upgrade to Professional
              </button>
            )}

            {showUpgradeToAnnual && (
              <button 
                className={styles.upgradeButton} 
                onClick={() => onUpgrade(isThereNextTier ? 'standard': 'professional', 'annual')}
                disabled={user?.role === UserRole.ASSISTANT}
              >
                Upgrade to Annual
              </button>
            )}
          </div>
        )}
      </div>
      <Divider margin={"16px 0 24px 0"} />
      <div className={styles.infoGrid}>
        <div className={styles.infoLabel}>Current Period:</div>
        <div>{currentPeriod.start} - {currentPeriod.end}</div>

        <div className={styles.infoLabel}>Term Behaviour:</div>
        <div>{termBehaviour}</div>

        <div className={styles.infoLabel}>Collection:</div>
        <div>{collection}</div>

        <div className={styles.infoLabel}>Renews On:</div>
        <div>{renewsOn}</div>

        <div className={styles.infoLabel}>Started On:</div>
        <div>{startedOn}</div>

        <div className={styles.infoLabel}>Latest Invoice:</div>
        <div className={styles.invoiceLink} onClick={handleViewInvoice}>{latestInvoice}</div>
      </div>

      {!isCanceled && !isExpired && (
        <div className={styles.nextInvoice}>
          <div className={styles.nextInvoiceHeader}>
            <h3 className={styles.nextInvoiceTitle}>Next Invoice on {nextInvoiceDate}</h3>
          </div>
          <Divider margin={"0"} />
          <div className={styles.lineItems}>
            {lineItems.map((item, index) => (
              <div key={index} className={styles.lineItem}>
                <div className={styles.lineItemDescription}>
                  {item.qty} x {item.description}
                </div>
                <div className={styles.lineItemPrice}>
                  ${item.price.toFixed(2)} {currency}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.totalRow}>
            <div className={styles.totalLabel}>Current Total:</div>
            <div className={styles.totalAmount}>${total.toFixed(2)} {currency}</div>
          </div>
        </div>
      )}

      <div className={styles.footer}>
        {(isCanceled || isExpired) ? (
          <Tooltip title="Please reach out to support to resubscribe">
            <button 
              className={styles.cancelButton} 
              onClick={onResubscribe}
              disabled={true}
            >
              Resubscribe
            </button>
          </Tooltip>
        ) : (
          <button 
            className={styles.cancelButton} 
            onClick={onCancel}
          >
            Cancel Subscription
          </button>
        )}
      </div>
    </div>
  );
};

export default SubscriptionCard;
